import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.vietclinics.com/api/v1';

const api = axios.create({ baseURL: BASE_URL, headers: { 'X-Platform': 'web' } });

// Public instance — no auth token (used for endpoints that must not receive a Bearer header)
const publicApi = axios.create({ baseURL: BASE_URL });

export const getEmailSettings = () => api.get('/admin/email-settings');
export const saveEmailSettings = (data) => api.post('/admin/email-settings', data);
/** @deprecated — backend now handles booking emails automatically */
export const sendBookingEmail = (data) => publicApi.post('/notifications/send-booking-email', data);

// Request interceptor — attach Bearer token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('vc_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — auto-refresh on 401
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refresh = localStorage.getItem('vc_refresh_token');
      if (refresh) {
        try {
          const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken: refresh });
          const { accessToken, refreshToken: newRefresh } = data.data;
          localStorage.setItem('vc_access_token', accessToken);
          localStorage.setItem('vc_refresh_token', newRefresh);
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch {
          localStorage.removeItem('vc_access_token');
          localStorage.removeItem('vc_refresh_token');
          localStorage.removeItem('vc_user');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ─── Clinics ─────────────────────────────────────────────────────────────────
export const getClinics = (params = {}) => api.get('/clinics', { params });
export const getClinicById = (id) => api.get(`/clinics/${id}`);
export const getClinicReviews = (id, params = {}) => api.get(`/clinics/${id}/reviews`, { params });

// ─── Categories & Treatments ─────────────────────────────────────────────────
export const getCategories = () => api.get('/categories');
export const getTreatments = (params = {}) => api.get('/treatments', { params });

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const loginUser = (email, password) => api.post('/auth/login', { email, password });
export const requestLoginOtp = (email) => publicApi.post('/auth/otp/request', { email });
export const verifyLoginOtp = (email, code) => publicApi.post('/auth/otp/verify', { email, code });
export const registerUser = (name, email, phone, password) =>
  api.post('/auth/register', { name, email, phone, password });
export const refreshTokens = (refreshToken) =>
  api.post('/auth/refresh', { refreshToken });

// ─── Bookings ─────────────────────────────────────────────────────────────────
export const createBooking = (data) => api.post('/bookings', data);
export const getBookings = (params = {}) => api.get('/bookings', { params });
export const cancelBooking = (id) => api.patch(`/bookings/${id}/cancel`);

// ─── Profile ─────────────────────────────────────────────────────────────────
export const getProfile = () => api.get('/profile');
export const updateProfile = (data) => api.patch('/profile', data);
export const changePassword = (data) => api.post('/profile/change-password', data);
export const uploadAvatar = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/profile/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteAvatar = () => api.delete('/profile/avatar');

// ─── Saved Clinics ────────────────────────────────────────────────────────────
export const getSavedClinics = () => api.get('/profile/saved-clinics');
export const saveClinic = (clinicId) => api.post('/profile/saved-clinics', { clinicId });
export const unsaveClinic = (clinicId) => api.delete(`/profile/saved-clinics/${clinicId}`);

// ─── Reviews ─────────────────────────────────────────────────────────────────
export const postReview = (data) => api.post('/reviews', data);

// ─── FAQ ─────────────────────────────────────────────────────────────────────
export const getFaqs = () => api.get('/faq');

// ─── Search ──────────────────────────────────────────────────────────────────
export const searchGlobal = (q, limit = 10) =>
  api.get('/search', { params: { q, limit } });

// ─── Clinic Registration & Auth ──────────────────────────────────────────────
export const registerClinic = (data) => api.post('/auth/clinic/register', data);
export const loginClinic = (email, password) =>
  api.post('/auth/clinic/login', { email, password });

// ─── Clinic Dashboard (authenticated as clinic staff) ────────────────────────
const clinicApi = axios.create({ baseURL: BASE_URL, headers: { 'X-Platform': 'web' } });
clinicApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('vc_clinic_access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const getClinicDashboardStats = () =>
  clinicApi.get('/clinic-dashboard/stats');
export const getClinicDashboardBookings = (params = {}) =>
  clinicApi.get('/clinic-dashboard/bookings', { params });
export const updateClinicBookingStatus = (bookingId, status, clinicNotes = '') =>
  clinicApi.patch(`/clinic-dashboard/bookings/${bookingId}/status`, { status, clinicNotes });
export const getClinicDashboardReviews = (params = {}) =>
  clinicApi.get('/clinic-dashboard/reviews', { params });
export const getClinicDashboardProfile = () =>
  clinicApi.get('/clinic-dashboard/profile');
export const updateClinicDashboardProfile = (data) =>
  clinicApi.patch('/clinic-dashboard/profile', data);

// ─── Clinic Dashboard: Doctors ────────────────────────────────────────────────
export const getClinicDoctors = () => clinicApi.get('/clinic-dashboard/doctors');
export const addClinicDoctor = (data) => clinicApi.post('/clinic-dashboard/doctors', data);
export const updateClinicDoctor = (id, data) => clinicApi.patch(`/clinic-dashboard/doctors/${id}`, data);
export const deleteClinicDoctor = (id) => clinicApi.delete(`/clinic-dashboard/doctors/${id}`);

// ─── Clinic Dashboard: Services ───────────────────────────────────────────────
export const getClinicServices = () => clinicApi.get('/clinic-dashboard/services');
export const addClinicService = (data) => clinicApi.post('/clinic-dashboard/services', data);
export const updateClinicService = (id, data) => clinicApi.patch(`/clinic-dashboard/services/${id}`, data);
export const deleteClinicService = (id) => clinicApi.delete(`/clinic-dashboard/services/${id}`);

// ─── Clinic Dashboard: Staff ──────────────────────────────────────────────────
export const getClinicStaff = () => clinicApi.get('/clinic-dashboard/staff');

// ─── Clinic Dashboard: File Upload ───────────────────────────────────────────
export const uploadClinicFile = (file, type) => {
  const formData = new FormData();
  formData.append('file', file);
  return clinicApi.post(`/clinic-dashboard/upload?type=${type}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Get best available image URL for a clinic */
export const getClinicImage = (clinic) =>
  clinic?.imageUrl ||
  clinic?.images?.[0]?.imageUrl ||
  'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=600&h=400&fit=crop';

/** Format USD price */
export const formatPriceUSD = (price) => {
  if (!price && price !== 0) return 'Contact';
  return `$${Number(price).toLocaleString()} USD`;
};

/** Translate category slug → specialty query keyword */
export const getCategorySpecialtyKey = (categorySlug) => {
  const map = {
    dental: 'dental',
    'cosmetic-surgery': 'cosmetic',
    'eye-care': 'eye',
    'hair-transplant': 'hair',
    'health-checkups': 'health',
    dermatology: 'skin',
    orthopedics: 'orthopedic',
    fertility: 'ivf',
    cardiology: 'cardiac',
    'general-medicine': 'medicine',
  };
  return map[categorySlug] || categorySlug;
};

// ─── Booking Detail (patient) ─────────────────────────────────────────────────
export const getBookingById = (id) => api.get(`/bookings/${id}`);

// ─── Cities ───────────────────────────────────────────────────────────────────
export const getCities = () => api.get('/clinics/meta/cities');

// ─── Doctor Profiles (public) ─────────────────────────────────────────────────
export const getDoctorById = (id) => api.get(`/doctors/${id}`);
export const getDoctorsByClinic = (clinicId) => api.get(`/doctors/clinic/${clinicId}`);

// ─── Locale ───────────────────────────────────────────────────────────────────
export const getCurrencies = () => publicApi.get('/locale/currencies');

// ─── Emergency Hotlines ───────────────────────────────────────────────────────
export const getEmergencyHotlines = () => publicApi.get('/emergency-hotlines');

// Maintenance mode status (public)
export const getMaintenanceStatus = () => api.get('/settings/maintenance');

// Site-wide contact info (public)
export const getContactInfo = () => publicApi.get('/settings/contact-info');

// ─── Contact Form ─────────────────────────────────────────────────────────────
export const submitContactForm = (data) => publicApi.post('/contact', data);

// ─── Articles / News ──────────────────────────────────────────────────────────
export async function getArticles(params = {}) {
  const query = new URLSearchParams();
  if (params.page)     query.set('page',       params.page);
  if (params.limit)    query.set('limit',       params.limit);
  if (params.category) query.set('category',    params.category);
  if (params.search)   query.set('search',      params.search);
  if (params.isFeatured !== undefined)
                       query.set('isFeatured',  String(params.isFeatured));
  const qs = query.toString();
  const res = await fetch(`${BASE_URL}/articles${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error('Failed to fetch articles');
  return res.json();
}

export async function getArticleBySlug(slug) {
  const res = await fetch(`${BASE_URL}/articles/${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error('Article not found');
  return res.json();
}

export default api;
